import { checkCustomerPaymentMethod, createSetupIntent } from "frontend/api";
import { useEffect, useState } from "react";
import { BillingPeriod } from "./billing-period-picker";
import consts, { MIN_PAID_SEATS, Plan } from "shared/consts";
import { InvoicePreview } from "shared/datamodel/schemas/billing";
import tracking from "frontend/tracking";
import { CheckoutSource } from "./checkout";
import { useAtomValue } from "jotai";
import { accountSubscribedCountAtom, invoicePreviewsAtom } from "state-atoms";
import { openCustomerPortal } from "frontend/billingUtils";
import { useApplyViewerRestricted } from "frontend/hooks/use-apply-viewer-restricted";

const SELECTED_PLAN = Plan.pro;

function getDropDownOptions(minValue: number, increment: number, length: number) {
  const array = [];
  for (let index = 0; index < length; index++) {
    array.push(minValue + index * increment);
  }
  return array;
}

export function useHasPaymentMethod() {
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);

  useEffect(() => {
    async function checkPaymentMethod() {
      const hasPaymentMethod = await checkCustomerPaymentMethod();
      setHasPaymentMethod(hasPaymentMethod);
    }
    checkPaymentMethod();
  }, []);

  return hasPaymentMethod;
}

export function useBillingHooks({
  seatsCount,
  source,
  type,
}: {
  seatsCount: number;
  source?: CheckoutSource;
  type: "page" | "modal";
}) {
  const applyRestrictedLimitation = useApplyViewerRestricted();
  const accountSubscriberCount = useAtomValue(accountSubscribedCountAtom);
  if (applyRestrictedLimitation) seatsCount = accountSubscriberCount;

  //atoms
  const invoicePreviewsAtomValue = useAtomValue(invoicePreviewsAtom);

  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod>("year");
  const [stripeSecret, setStripeSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const hasPaymentMethod = useHasPaymentMethod();
  const [confirmPayment, setConfirmPayment] = useState<boolean>(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState<boolean>(false);
  const [seatsToBuy, setSeatsToBuy] = useState(seatsCount < MIN_PAID_SEATS ? MIN_PAID_SEATS : seatsCount);
  const [message, setMessage] = useState<string | null>(null);
  const [invoiceByPlanCache, setInvoiceByPlanCache] = useState<Record<BillingPeriod, InvoicePreview> | null>(null);
  const [yearlySaving, setYearlySaving] = useState<number>(0);
  const [dropDownOptions, setDropDownOptions] = useState<number[]>([]);
  const [seatsMultiplyFactor, setSeatsMultiplyFactor] = useState<number>(1); //We calcualte client-side the subtotal of the invoice every time the user changes the number of seats. We do it by multiplying the base number by the delta of the seats after the change.
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState<string | null>(null);
  async function previewInvoice(invalidateCache?: boolean) {
    if (invalidateCache) {
      setInvoiceByPlanCache(null);
    }
    //if invoice for plan not found in state cache, update it
    if (invalidateCache || (invoicePreviewsAtomValue && seatsToBuy > 0 && !invoiceByPlanCache)) {
      const invoices = invoicePreviewsAtomValue;
      if (!invoices) {
        return;
      }
      const proInvoicePreview = invoices.find((invoice) => invoice.plan === Plan.pro && invoice.interval === "month");
      const proYearlyInvoicePreview = invoices.find(
        (invoice) => invoice.plan === Plan.pro && invoice.interval === "year"
      );

      if (!proInvoicePreview || !proYearlyInvoicePreview) {
        return;
      }

      const updatedInvoice = {
        month: {
          dueToday: proInvoicePreview.dueToday,
          dueNextPeriod: proInvoicePreview.dueNextPeriod,
          nextPeriod: proInvoicePreview.nextPeriod,
          trialEndDate: proInvoicePreview.trialEndDate,
        } as InvoicePreview,
        year: {
          dueToday: proYearlyInvoicePreview.dueToday,
          dueNextPeriod: proYearlyInvoicePreview.dueNextPeriod,
          nextPeriod: proYearlyInvoicePreview.nextPeriod,
          trialEndDate: proYearlyInvoicePreview.trialEndDate,
        } as InvoicePreview,
      };

      setInvoiceByPlanCache(updatedInvoice);
    }
  }

  async function onPayButtonClicked() {
    tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "trial-modal-continue-clicked", SELECTED_PLAN);
    const subscriptionValue = invoiceByPlanCache ? invoiceByPlanCache[selectedPeriod].dueNextPeriod : 0;
    tracking.reportGoogleAnalyticsConversion(consts.GA_CONVERSION_TYPES.PAID_PLAN_SELECTED, subscriptionValue);
    const planInvoicePreview = invoiceByPlanCache && invoiceByPlanCache[selectedPeriod];
    setShowCheckoutForm(true);
    setLoading(true);

    // Check if we should handle via customer portal (very low charges)
    if (
      hasPaymentMethod ||
      (planInvoicePreview &&
        !planInvoicePreview.trialEndDate &&
        planInvoicePreview.dueToday < consts.MIN_STRIPE_ALLOWED_AMOUNT)
    ) {
      openCustomerPortal({ type: "upgrade", planId: SELECTED_PLAN, interval: selectedPeriod });
    } else {
      // Always create setup intent and show checkout form (whether they have payment method or not)
      const { clientSecret, customerSessionClientSecret } = await createSetupIntent({
        newPlan: SELECTED_PLAN,
        seatsCount: seatsToBuy,
        billingInterval: selectedPeriod,
      });
      setStripeSecret(clientSecret);
      setCustomerSessionClientSecret(customerSessionClientSecret);
      setLoading(false);
    }
  }

  useEffect(() => {
    previewInvoice(true);
    setSeatsMultiplyFactor(Math.max(MIN_PAID_SEATS, seatsToBuy) / Math.max(MIN_PAID_SEATS, seatsCount));
  }, [seatsToBuy, seatsCount]);

  useEffect(() => {
    if (seatsCount > 0) {
      const dropDownOptions = getDropDownOptions(seatsCount < MIN_PAID_SEATS ? MIN_PAID_SEATS : seatsCount, 1, 50);
      setDropDownOptions(dropDownOptions);
    }
    if (seatsCount < MIN_PAID_SEATS) {
      setSeatsToBuy(MIN_PAID_SEATS);
    } else {
      setSeatsToBuy(seatsCount);
    }
  }, [seatsCount]);

  useEffect(() => {
    if (type === "modal") {
      return;
    }

    previewInvoice();
    if (invoiceByPlanCache) {
      const monthlyPlan = invoiceByPlanCache["month"];
      const yearlyPlan = invoiceByPlanCache["year"];
      const yearlySaving = (yearlyPlan.dueNextPeriod / (monthlyPlan.dueNextPeriod * 12)) * 100;
      setYearlySaving(yearlySaving);
    }
  }, [invoiceByPlanCache]);

  useEffect(() => {
    if (type === "page") {
      return;
    }

    previewInvoice();
    if (invoiceByPlanCache) {
      const monthlyPlan = invoiceByPlanCache["month"];
      const yearlyPlan = invoiceByPlanCache["year"];
      const yearlySaving =
        monthlyPlan.dueNextPeriod * 12 * seatsMultiplyFactor - yearlyPlan.dueNextPeriod * seatsMultiplyFactor;
      setYearlySaving(yearlySaving);
    }
  }, [invoiceByPlanCache, seatsMultiplyFactor]);

  useEffect(() => {
    previewInvoice();
    if (invoicePreviewsAtomValue) {
      tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, `payment-${type}-invoice-previews-loaded`, source);
    }
  }, [invoicePreviewsAtomValue]);

  return {
    invoiceByPlanCache,
    yearlySaving,
    dropDownOptions,
    seatsMultiplyFactor,
    selectedPeriod,
    showCheckoutForm: showCheckoutForm && stripeSecret,
    customerSessionClientSecret,
    stripeSecret,
    loading,
    confirmPayment,
    message,
    seatsToBuy,
    invoicePreviewsAtomValue,
    setSelectedPeriod,
    setShowCheckoutForm,
    setConfirmPayment,
    setMessage,
    setSeatsToBuy,
    onPayButtonClicked,
  };
}
